import {
  DeleteTwoTone,
  ExportOutlined,
  PlusSquareFilled,
} from "@ant-design/icons";
import {
  ConfigOf,
  InstallSpec,
  ItemConfigOf,
} from "@p0security/shared/install/types";
import { IntegrationStatus } from "@p0security/shared/integrations/shared";
import { Button, Flex, Input, Spin, Typography } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import { useNavigateWithSearchParams } from "components/Assessment/hooks/useNavigateWithSearchParams";
import { AuthFetch } from "components/Login/hook";
import { FrontendInstallContext } from "install/types";
import { capitalize, sortBy } from "lodash";
import pluralize from "pluralize";
import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";

import { DeleteModal } from "../components/DeleteModal";
import { Config, configPath } from "./Config";
import { InstallProps } from "./Install";
import { New } from "./New";
import { Singleton } from "./Singleton";

export type ComponentProps<T extends InstallSpec> = InstallProps<T> & {
  authFetch: AuthFetch;
  canEdit: boolean;
  config: ConfigOf<T>;
  context: FrontendInstallContext<ConfigOf<T>>;
  componentKey: string;
  docsUrl?: string;
  error: string | undefined;
  isFetching: boolean;
};

export const NEW_SENTINEL = "_new";

const INCOMPLETE_STATE_PRIORITY: Record<IntegrationStatus, number> = {
  error: 1,
  stage: 2,
  configure: 3,
  installed: 4,
};

const DeleteAction: React.FC<{
  id: string;
  deleteItem: (id: string) => Promise<void>;
}> = ({ id, deleteItem }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const toggleDeleteModal = useCallback(
    () => setIsDeleteModalOpen(!isDeleteModalOpen),
    [isDeleteModalOpen]
  );
  const confirmDelete = useCallback(async () => {
    setIsDeleting(true);
    setIsDeleteModalOpen(false);
    await deleteItem(id);
    setIsDeleting(false);
  }, [deleteItem, id]);

  return (
    <>
      <Button
        type="link"
        icon={<DeleteTwoTone />}
        loading={isDeleting}
        onClick={isDeleting ? undefined : toggleDeleteModal}
      />
      <DeleteModal
        id={id}
        isOpen={isDeleteModalOpen}
        toggleOpen={toggleDeleteModal}
        onRemove={confirmDelete}
      />
    </>
  );
};

const itemColumns = (props: {
  title: string;
  canEdit: boolean;
  deleteItem: (item: string) => Promise<void>;
}): ColumnsType<ItemConfigOf<any> & { key: string }> => [
  {
    key: "id",
    title: props.title,
    render: (_, { key, label }) => (
      <Link to={encodeURIComponent(key)} relative="path">
        {label ? `${label} (${key})` : key}
      </Link>
    ),
  },
  {
    key: "state",
    title: "State",
    // TODO: make pretty
    render: (_, { state }) =>
      state === "installed" ? "Installed" : "In progress",
  },
  {
    key: "actions",
    title: "",
    render: (_, { key }) =>
      props.canEdit && <DeleteAction id={key} deleteItem={props.deleteItem} />,
  },
];

export const Component = <T extends InstallSpec>(props: ComponentProps<T>) => {
  const { id: encodedId } = useParams<{
    id: string;
  }>();
  const [filter, setFilter] = useState("");
  const id = encodedId ? decodeURIComponent(encodedId) : undefined;
  const navigate = useNavigateWithSearchParams();
  const {
    authFetch,
    canEdit,
    componentKey,
    components,
    config,
    context,
    docsUrl,
    itemKind,
  } = props;
  const component = components?.[componentKey];
  const items = config?.[componentKey];

  const isSingleton = component.type === "singleton";

  const deleteItem = useCallback(
    async (id: string) => {
      await authFetch(configPath({ ...props, id }), { method: "DELETE" });
    },
    [authFetch, props]
  );

  const updateFilter = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setFilter(event.target.value),
    []
  );

  const columns = useMemo(
    () =>
      itemColumns({
        title: capitalize(itemKind(componentKey)),
        canEdit,
        deleteItem,
      }),
    [canEdit, componentKey, deleteItem, itemKind]
  );

  const data = useMemo(
    () =>
      sortBy(
        Object.entries(items ?? {})
          .map(([key, item]) => ({
            ...item,
            key,
          }))
          .filter(
            ({ key, label }) =>
              !filter ||
              key.toLowerCase().includes(filter) ||
              label?.toLowerCase().includes(filter)
          ),
        [({ state }) => INCOMPLETE_STATE_PRIORITY[state], "key"]
      ),
    [filter, items]
  );

  const item = useMemo(
    () => (id !== undefined && id !== NEW_SENTINEL ? items?.[id] : undefined),
    [items, id]
  );

  const installNew = useCallback(
    () => navigate("_new", { relative: "path" }),
    [navigate]
  );

  return (
    <>
      <Flex
        justify="space-between"
        align="flex-start"
        style={{ marginBottom: 16 }}
      >
        <div>
          <Typography.Title level={3} style={{ marginBottom: 8 }}>
            {component?.label}
          </Typography.Title>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {component?.description}
          </Typography.Paragraph>
        </div>
        {docsUrl && (
          <Button
            type="link"
            size="small"
            className="button-link"
            href={docsUrl}
            target="_blank"
            rel="noreferrer"
            icon={<ExportOutlined />}
          >
            View Documentation
          </Button>
        )}
      </Flex>
      {isSingleton ? (
        <Singleton {...props} component={component} />
      ) : id === NEW_SENTINEL ? (
        <New {...props} component={component} context={context} />
      ) : id ? (
        item ? (
          <Config
            {...props}
            component={component}
            context={context}
            id={id}
            item={item}
          />
        ) : (
          <Spin />
        )
      ) : (
        <>
          <Typography.Title level={4}>
            Installed {pluralize(itemKind(componentKey))}
          </Typography.Title>
          <div
            style={{
              marginBottom: 16,
              width: "100%",
              display: "flex",
              gap: "1em",
              justifyContent: "space-between",
            }}
          >
            <Input
              placeholder={`Filter ${pluralize(itemKind(componentKey))}...`}
              value={filter}
              onChange={updateFilter}
              style={{ maxWidth: "25em" }}
            />
            {!canEdit ? null : !component.maxItems ||
              component.maxItems > data.length ? (
              <Button
                onClick={installNew}
                type="primary"
                icon={<PlusSquareFilled />}
              >
                Add {itemKind(componentKey)}
              </Button>
            ) : (
              `A maximum of ${component.maxItems} ${pluralize(
                itemKind(componentKey),
                component.maxItems
              )} may be installed`
            )}
          </div>
          <Table<ItemConfigOf<any> & { key: string }>
            columns={columns}
            dataSource={data}
            pagination={{ hideOnSinglePage: true }}
          />
        </>
      )}
    </>
  );
};
